// Claim a guest pass (spec/15, F-SES-07): a walk-in who played on a ticket from the counter types its code to move what is
// left of it, and the sessions they played, into their own account. The code is six characters on the receipt; the page
// opens with it filled in from `?code=` (a link or a QR on the receipt can carry it), and a signed-out visitor is sent to sign
// in and brought back with it kept (the session gate puts the whole URL in `redirect`). The server answers every refusal for a
// code nobody holds the same way, so this page never says why a code is not there beyond what the three answers allow.

import { GUEST_CLAIM_WINDOW_DAYS, GUEST_TICKET_CODE_PATTERN, normaliseGuestTicketCode } from '@clavian/contracts';
import { useTranslation } from '@clavian/i18n/react';
import { Button, TextField, useToast } from '@clavian/ui-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { problemCode } from '../api/problems';
import { queryKeys } from '../api/queries';
import { DEFAULT_VENUE_LOCALE } from '../i18n';
import { formatterFor, shortDuration } from '../lib/format';
import { Page } from '../shell/Page';

/** What a refused claim says, from the problem's `code`; anything else (the network, an answer this page does not know) is the plain "did not work". */
function failureKey(error: unknown): string {
  switch (problemCode(error)) {
    case 'guest_ticket_invalid':
      return 'guestPass.invalid';
    case 'guest_claim_unavailable':
      return 'guestPass.unavailable';
    case 'rate_limited':
      return 'guestPass.rateLimited';
    default:
      return 'guestPass.failed';
  }
}

export function GuestPassPage(): ReactNode {
  const { t } = useTranslation('player');
  const api = useApi();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const search = useSearch({ from: '/app/guest-pass' });
  // The code as typed, shown without dashes or spaces; what is sent is the same, read the way the server reads it.
  const [typed, setTyped] = useState(() => normaliseGuestTicketCode(search.code ?? ''));
  const [shapeError, setShapeError] = useState<string | undefined>(undefined);

  const claim = useMutation({
    mutationFn: (code: string) => api.claimGuestPass({ body: { code } }),
    // The ticket's pass is now one of the player's, and its sessions are in their history.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.passes }),
        queryClient.invalidateQueries({ queryKey: queryKeys.playSessions }),
        queryClient.invalidateQueries({ queryKey: queryKeys.live }),
      ]);
      // The claim landing: one burst; the page below says what was added.
      show({ title: t('guestPass.claimedToast'), tone: 'ok' });
    },
  });

  const submit = (): void => {
    const code = normaliseGuestTicketCode(typed);
    if (code === '') return setShapeError(t('guestPass.required'));
    if (!GUEST_TICKET_CODE_PATTERN.test(code)) return setShapeError(t('guestPass.malformed'));
    setShapeError(undefined);
    claim.mutate(code);
  };

  const done = claim.data;
  const fmt = formatterFor(DEFAULT_VENUE_LOCALE.currency);

  return (
    <Page
      title={t('guestPass.title')}
      description={t('guestPass.subtitle')}
      actions={
        <Link to="/passes" className="pw-btn pw-btn--secondary">
          {t('guestPass.back')}
        </Link>
      }
    >
      <section className="pw-panel pw-guestpass" aria-labelledby="pw-guestpass-heading">
        {done === undefined ? (
          <>
            <h2 className="pw-section-title" id="pw-guestpass-heading">
              {t('guestPass.formTitle')}
            </h2>
            {claim.isError ? (
              <p className="pw-alert" role="alert">
                {t(failureKey(claim.error), { days: GUEST_CLAIM_WINDOW_DAYS })}
              </p>
            ) : null}
            <form
              className="pw-form"
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <TextField
                label={t('guestPass.code')}
                description={t('guestPass.codeHint')}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                name="code"
                value={typed}
                onChange={(event) => setTyped(event.target.value.toUpperCase())}
                error={shapeError}
              />
              <div className="pw-form__actions">
                <Button type="submit" variant="primary" busy={claim.isPending}>
                  {t('guestPass.claim')}
                </Button>
              </div>
            </form>
            <p className="pw-guestpass__note">{t('guestPass.note', { days: GUEST_CLAIM_WINDOW_DAYS })}</p>
          </>
        ) : (
          <div className="pw-notice" role="status">
            <h2 className="pw-section-title" id="pw-guestpass-heading">
              {t('guestPass.doneTitle')}
            </h2>
            <p>
              {done.movedSeconds > 0
                ? t('guestPass.doneTime', { duration: shortDuration(done.movedSeconds), pass: done.passName, venue: done.venue.name })
                : t('guestPass.doneNoTime', { pass: done.passName, venue: done.venue.name })}
            </p>
            {done.passExpiresAt === null ? null : <p>{t('guestPass.doneUntil', { date: fmt.date(done.passExpiresAt, 'medium') })}</p>}
            {done.sessions > 0 ? <p>{t('guestPass.doneSessions', { count: done.sessions })}</p> : null}
            <p>
              <Link to="/passes" className="pw-btn pw-btn--primary">
                {t('guestPass.toPasses')}
              </Link>
            </p>
          </div>
        )}
      </section>
    </Page>
  );
}
